import React from "react";
import { CardSkeleton } from "../../../components/skeleton/ListSkeleton";
import { AppointmentKpiKey, SourceKey, getAppointments } from "../analyticsApi";
import { formatDecimal, formatNumber, formatPct, formatRs, higherIsBetterFor, seriesIsEmpty, sourceLabel, weekdayLabel } from "../analyticsUtils";
import { useAnalyticsPeriod } from "../useAnalyticsPeriod";
import { isBillingEnabled } from "../../../utils/clinicFeatures";
import { useAnalyticsFetch } from "../useAnalyticsFetch";
import PeriodPicker from "../components/PeriodPicker";
import KpiCard from "../components/KpiCard";
import Heatmap from "../components/Heatmap";
import EmptyState from "../components/EmptyState";
import { CategoryBarChart, PeriodLineChart } from "../components/charts";
import { ChartCard, EmptyRow, TD, TD_NUM, TH, TH_NUM, TR, TableCard } from "../components/ui";

const KPIS: { key: AppointmentKpiKey; label: string; fmt: (v: number | null) => string }[] = [
  { key: "total", label: "Appointments", fmt: formatNumber },
  { key: "completed", label: "Completed", fmt: formatNumber },
  { key: "noShowRate", label: "No-show rate", fmt: formatPct },
  { key: "cancellationRate", label: "Cancellation rate", fmt: formatPct },
  { key: "sessionFill", label: "Session fill", fmt: formatPct },
  { key: "waitMinutesAvg", label: "Average wait", fmt: (v) => (v == null ? "—" : `${formatDecimal(v)} min`) },
  { key: "leadDaysAvg", label: "Booking lead time", fmt: (v) => (v == null ? "—" : `${formatDecimal(v)} days`) },
];

const SOURCES: SourceKey[] = ["pre_booked", "walk_in", "online", "other"];

export default function AnalyticsAppointments() {
  const period = useAnalyticsPeriod();
  const { data, loading } = useAnalyticsFetch(() => getAppointments({ from: period.from, to: period.to }), [period.from, period.to]);

  const noData = !loading && data != null && (data.totals.total === 0 || seriesIsEmpty(data.series, ["total"]));

  return (
    <div className="space-y-4 text-slate-800 dark:text-slate-200">
      <PeriodPicker period={period} />

      {loading || !data ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <CardSkeleton cards={8} height="h-28" />
        </div>
      ) : noData ? (
        <EmptyState />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {KPIS.map((k) => (
              <KpiCard key={k.key} label={k.label} value={k.fmt(data.kpis[k.key]?.current ?? null)} change={data.kpis[k.key]?.change ?? null} higherIsBetter={higherIsBetterFor(k.key)} />
            ))}
            {isBillingEnabled() && (
              <KpiCard label="Revenue per open hour" value={formatRs(data.revenuePerOpenHour)} hint={`${formatNumber(Math.round(data.totals.openMinutes / 60))} open hours in the period`} />
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold mb-3">By source</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {SOURCES.map((s) => (
                <KpiCard key={s} label={sourceLabel(s)} value={formatNumber(data.bySource[s]?.current ?? null)} change={data.bySource[s]?.change ?? null} />
              ))}
            </div>
          </div>

          <ChartCard title="Appointments over time" hint={data.period.granularity === "month" ? "Per month" : "Per day"}>
            <PeriodLineChart
              series={data.series}
              specs={[
                { key: "total", label: "Booked" },
                { key: "completed", label: "Completed" },
                { key: "noShow", label: "No-show" },
              ]}
            />
          </ChartCard>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <ChartCard title="Busy hours" hint="Appointments by weekday and start hour">
                <Heatmap grid={data.heatmap} />
              </ChartCard>
            </div>
            <ChartCard title="No-show rate by weekday">
              <CategoryBarChart labels={data.byWeekday.map((w) => weekdayLabel(w.weekday))} values={data.byWeekday.map((w) => w.noShowRate)} label="No-show rate" format="pct" height={260} />
            </ChartCard>
          </div>

          <TableCard title="By doctor">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-600">
                  <th className={TH}>Doctor</th>
                  <th className={TH_NUM}>Appointments</th>
                  <th className={TH_NUM}>Completed</th>
                  <th className={TH_NUM}>No-shows</th>
                  <th className={TH_NUM}>No-show rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
                {data.byDoctor.length === 0 ? (
                  <EmptyRow colSpan={5} />
                ) : (
                  data.byDoctor.map((d) => (
                    <tr key={d.doctorId} className={TR}>
                      <td className={TD}>{d.name}</td>
                      <td className={TD_NUM}>{formatNumber(d.appointments)}</td>
                      <td className={TD_NUM}>{formatNumber(d.completed)}</td>
                      <td className={TD_NUM}>{formatNumber(d.noShow)}</td>
                      <td className={TD_NUM}>{formatPct(d.noShowRate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </TableCard>
        </>
      )}
    </div>
  );
}
